// Counts skeletons that are still on screen *after* their data arrived —
// the minimum-visible hold of the shared phase machine
// (`@bitacorapro/shared/loading`, phase "holding"). In that window no
// query is fetching, so useIsFetching() reads 0 and useAppReadySignal
// would otherwise declare the page ready and let design:snapshot
// serialize a skeleton as the "populated" state. useSkeletonPhase
// acquires a hold for exactly the "holding" phase; useAppReady adds the
// count to its `busy`.
//
// Module-level on purpose: one app, one document, one snapshot signal.
// Read through useSyncExternalStore so the count is tear-free under
// concurrent rendering.
import { useSyncExternalStore } from "react";

let count = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/**
 * Registers one active hold. The returned release is idempotent, so a
 * cleanup that runs twice (StrictMode, unmount after a phase change)
 * can never drive the count below what is really on screen.
 */
export function acquireHold(): () => void {
  count += 1;
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    count -= 1;
    emit();
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): number {
  return count;
}

export function useHoldCount(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
